<template>
  <!-- The painted Altar IS the room (altar redesign 2026-10-27). Before it is
       painted, only the gold outline of an empty altar is there; while it
       paints, the outline draws itself and breathes; then colour spreads out
       from the candles (an animated mask radius). Reduced motion: crossfades.
       While "Make the space yours" is open (2026-10-30), `look` lights the room
       as the live preview: the chosen mood's scene and light, the candles, the
       Sun and Moon in the outline's two circles, drawn in the chosen style
       (AltarScenes holds the drawings). It stays faintly while it paints. -->
  <div class="room" :class="[`is-${phase}`, { arrive: arrive && !reduced, rm: reduced, 'has-img': !!shownUrl, 'has-look': !!look, 'look-real': !!look && sk === 'realistic' }]" aria-hidden="true">
    <div class="cv">
      <svg v-if="look" class="lit" :class="[`st-${sk}`, { dip }]" viewBox="0 0 1000 1080" preserveAspectRatio="xMidYMid slice"
           focusable="false" :style="lightVars">
        <defs>
          <linearGradient id="apf-pvSky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color: var(--m1)" /><stop offset="1" style="stop-color: var(--m2)" /></linearGradient>
          <radialGradient id="apf-pvGlow"><stop offset="0" style="stop-color: var(--m3); stop-opacity: .55" /><stop offset="1" style="stop-color: var(--m3); stop-opacity: 0" /></radialGradient>
          <radialGradient id="apf-pvArch" cx=".5" cy=".75" r=".8"><stop offset="0" style="stop-color: var(--m3); stop-opacity: .34" /><stop offset="1" style="stop-color: var(--m1); stop-opacity: .15" /></radialGradient>
          <linearGradient id="apf-pvWax" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#f6e8c8" /><stop offset=".6" stop-color="#e9d6ae" /><stop offset="1" stop-color="#b89a6a" /></linearGradient>
          <linearGradient id="apf-pvTable" x1="0" y1="0" x2="0" y2="1"><stop offset="0" style="stop-color: var(--m2)" /><stop offset="1" stop-color="#05020b" /></linearGradient>
          <linearGradient id="apf-pvFade" x1="0" y1="0" x2="0" y2="1"><stop offset=".55" stop-color="#fff" /><stop offset="1" stop-color="#000" /></linearGradient>
          <mask id="apf-pvSceneMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="780"><rect width="1000" height="780" fill="url(#apf-pvFade)" /></mask>
          <filter id="apf-pvSoft" x="-5%" y="-5%" width="110%" height="110%"><feGaussianBlur stdDeviation="7" /></filter>
        </defs>
        <g class="art" :filter="`url(#apf-f-${sk}-lg)`">
          <rect width="1000" height="1080" fill="url(#apf-pvSky)" />
          <g mask="url(#apf-pvSceneMask)" filter="url(#apf-pvSoft)">
            <svg v-for="id in SCENES" :key="id" class="scn" :class="{ on: id === scene }" :data-s="id" x="0" y="0" width="1000" height="780"
                 viewBox="0 0 200 120" preserveAspectRatio="xMidYMid slice"><use :href="`#apf-sc-${id}`" /></svg>
          </g>
          <ellipse :key="breath" class="pv-glow" :class="{ breathe: breath > 0 }" cx="500" cy="520" rx="440" ry="400" fill="url(#apf-pvGlow)" />
          <path :d="LINES[0]" fill="url(#apf-pvArch)" />
          <template v-if="sun">
            <circle cx="578" cy="262" r="48" fill="rgba(13,5,32,.5)" />
            <text class="glyph" x="578" y="266" text-anchor="middle" dominant-baseline="middle" font-size="58" fill="#ffe7b5">{{ sun }}</text>
          </template>
          <rect x="140" y="684" width="720" height="396" fill="url(#apf-pvTable)" />
          <path :d="LINES[1]" fill="#0b0612" fill-opacity=".92" />
          <path :d="LINES[5]" style="fill: var(--m4)" fill-opacity=".55" />
          <circle v-for="c in CANDLES" :key="'h' + c[0]" :cx="c[0]" :cy="c[1] - 34" r="78" fill="url(#apf-g-halo)" />
          <rect x="345" y="540" width="34" height="105" fill="url(#apf-pvWax)" />
          <rect x="477" y="468" width="46" height="177" fill="url(#apf-pvWax)" />
          <rect x="623" y="515" width="34" height="130" fill="url(#apf-pvWax)" />
          <path v-for="c in CANDLES" :key="'f' + c[0]" class="apf-fl" :d="bigFlame(c[0], c[1])" fill="url(#apf-g-flame)" />
          <template v-if="moon">
            <circle cx="500" cy="860" r="84" fill="rgba(13,5,32,.55)" />
            <text class="glyph" x="500" y="866" text-anchor="middle" dominant-baseline="middle" font-size="96" fill="#ffe7b5" opacity=".9">{{ moon }}</text>
          </template>
          <rect class="grain" width="1000" height="1080" filter="url(#apf-f-grain-lg)" style="mix-blend-mode: soft-light" />
        </g>
        <g class="lead" v-html="LEAD"></g>
        <rect class="vig" width="1000" height="1080" fill="url(#apf-pvVig)" />
        <g class="motes">
          <circle v-for="m in motes" :key="m.id" class="mote" :cx="m.x" :cy="m.y" :r="m.r" fill="#ffe7b5"
                  :style="{ '--dx': m.dx + 'px', animationDelay: m.delay + 's' }" />
        </g>
      </svg>
      <img v-if="shownUrl" :src="shownUrl" class="room-img" alt="" :style="maskStyle" draggable="false" />
      <svg class="sk" viewBox="0 0 1000 1080" preserveAspectRatio="xMidYMid slice" fill="none" stroke="#f0d9a3"
           stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
        <path pathLength="1" d="M320 625V340Q320 172 500 142Q680 172 680 340V625" />
        <path pathLength="1" d="M128 684L168 642H832L872 684Z" />
        <path pathLength="1" d="M140 684V1080M860 684V1080" />
        <circle pathLength="1" cx="578" cy="262" r="48" />
        <path pathLength="1" d="M345 645V540H379V645M477 645V468H523V645M623 645V515H657V645" />
        <path pathLength="1" d="M500 412C512 432 512 452 500 462C488 452 488 432 500 412Z" />
        <path pathLength="1" d="M182 630Q248 672 314 630Q248 618 182 630Z" />
        <circle pathLength="1" cx="500" cy="860" r="84" />
      </svg>
    </div>
    <div class="veil"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { moodLook, styleKey, MOOD_LOOK } from '@/utils/altar-rite'
import { leading } from '@/utils/altar-scenes'
import { SIGNS, GLYPHS } from '@/utils/birth-ask'

const props = defineProps({
  imageUrl: { type: String, default: '' },
  // dark | drawing | shimmer | revealing | painted
  phase: { type: String, default: 'dark' },
  arrive: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
  // The paint form's choices { mood, style, paths } (null: the faint outline only).
  look: { type: Object, default: null },
  chart: { type: Object, default: null },    // { sun, moon }: their glyphs sit in the outline's circles
})
const emit = defineEmits(['revealed'])

// ── the lit room (the paint form's live preview) ─────────────────────────────
const LINES = ['M320 625V340Q320 172 500 142Q680 172 680 340V625', 'M128 684L168 642H832L872 684Z', 'M140 684V1080M860 684V1080',
  'M345 645V540H379V645M477 645V468H523V645M623 645V515H657V645', 'M500 412C512 432 512 452 500 462C488 452 488 432 500 412Z',
  'M182 630Q248 672 314 630Q248 618 182 630Z']
const CANDLES = [[362, 540], [500, 468], [640, 515]]
const SCENES = [...new Set(Object.values(MOOD_LOOK).map((l) => l[0]))]
const LEAD = leading(1000, 1080, 9, 13)
const bigFlame = (cx, t) => `M${cx} ${t - 70}C${cx + 22} ${t - 38} ${cx + 19} ${t - 12} ${cx} ${t - 3}C${cx - 19} ${t - 12} ${cx - 22} ${t - 38} ${cx} ${t - 70}Z`
const glyphOf = (s) => (SIGNS.includes(s) ? GLYPHS[SIGNS.indexOf(s)] : '')
const sun = computed(() => glyphOf(props.chart && props.chart.sun))
const moon = computed(() => glyphOf(props.chart && props.chart.moon))
const scene = computed(() => moodLook(props.look && props.look.mood)[0])
// The light, as registered colours on the room so it glides between moods.
const lightVars = computed(() => {
  const l = moodLook(props.look && props.look.mood)
  return { '--m1': l[1], '--m2': l[2], '--m3': l[3], '--m4': l[4] }
})

// The style: the room dips and returns in the new treatment.
const sk = ref(styleKey(props.look && props.look.style))
const dip = ref(false)
let dipT = 0
// Mood: the glow breathes once. Mood, style or a new path: a few gold motes rise.
const breath = ref(0)
const motes = ref([])
let moteId = 0
const moteTimers = []
function rise (n) {
  if (props.reduced) return
  for (let i = 0; i < n; i++) {
    const id = ++moteId
    motes.value.push({ id, x: 360 + Math.random() * 280, y: 520 + Math.random() * 120, r: 4 + Math.random() * 5, dx: (Math.random() - 0.5) * 80, delay: i * 0.12 })
    moteTimers.push(setTimeout(() => { motes.value = motes.value.filter((m) => m.id !== id) }, 3200))
  }
}
watch(() => props.look, (now, was) => {
  if (!now) return
  const k = styleKey(now.style)
  if (!was) { sk.value = k; return }
  if (now.mood !== was.mood) { if (!props.reduced) breath.value++; rise(5) }
  if (k !== sk.value) {
    clearTimeout(dipT)
    if (props.reduced) sk.value = k
    else { dip.value = true; dipT = setTimeout(() => { sk.value = k; dip.value = false }, 220) }
    rise(3)
  }
  if ((now.paths || []).length > (was.paths || []).length) rise(3)
})

// The image shows only once it is painted or being revealed; while the rite
// draws the outline, the room stays empty even if an older painting exists.
const shownUrl = computed(() => (['revealing', 'painted'].includes(props.phase) ? props.imageUrl : ''))

const radius = ref(-1)   // -1: no mask
const maskStyle = computed(() => {
  if (radius.value < 0) return {}
  const r = radius.value
  const m = `radial-gradient(circle at 50% 56%, #000 ${r}%, transparent ${r + 24}%)`
  return { WebkitMaskImage: m, maskImage: m }
})

let raf = 0
let timer = 0
function reveal () {
  cancelAnimationFrame(raf)
  clearTimeout(timer)
  if (props.reduced) {
    radius.value = -1
    timer = setTimeout(() => emit('revealed'), 400)
    return
  }
  radius.value = 0
  const t0 = performance.now()
  const D = 2400
  const step = (t) => {
    const p = Math.min(1, (t - t0) / D)
    const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
    radius.value = e * 130
    if (p < 1) raf = requestAnimationFrame(step)
    else { radius.value = -1; timer = setTimeout(() => emit('revealed'), 300) }
  }
  raf = requestAnimationFrame(step)
}
watch(() => props.phase, (p) => { if (p === 'revealing') reveal(); else if (p !== 'revealing') radius.value = -1 }, { immediate: true })
onBeforeUnmount(() => { cancelAnimationFrame(raf); clearTimeout(timer); clearTimeout(dipT); moteTimers.forEach(clearTimeout) })
</script>

<style scoped>
.room { position: absolute; inset: 0; pointer-events: none; overflow: hidden; background: #07040f; }
.cv { position: absolute; inset: 0; }
/* The whole painting, never cropped (the box is square, like the paintings). */
.room-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; object-position: 50% 50%; display: block; }
.has-img.is-revealing .room-img { animation: room-in .5s ease both; }
.sk { position: absolute; inset: 0; width: 100%; height: 100%; opacity: .2; transition: opacity 1.4s ease; }
.sk * { stroke-dasharray: 1 0; }
.is-dark .sk { opacity: .16; }
.is-drawing .sk { opacity: .9; }
.is-drawing .sk * { stroke-dasharray: 1 1; stroke-dashoffset: 1; animation: room-draw 2.2s cubic-bezier(.4, .1, .3, 1) forwards; }
.is-drawing .sk *:nth-child(3n+1) { animation-delay: .25s; }
.is-drawing .sk *:nth-child(3n+2) { animation-delay: .6s; }
.is-shimmer .sk { opacity: .9; animation: room-shim 1.6s ease-in-out infinite; }
.is-revealing .sk { opacity: 0; transition-duration: .9s; }
.is-painted .sk { opacity: 0; }
.veil { position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(13, 5, 32, .82) 0%, rgba(13, 5, 32, .45) 14%, rgba(13, 5, 32, 0) 26%, rgba(13, 5, 32, 0) 50%, rgba(13, 5, 32, .9) 75%, #0d0520 92%); }
.arrive .cv { animation: room-eyes 1.6s ease-out both; }
/* The lit room under the outline (the paint form's preview). */
.lit { position: absolute; inset: 0; width: 100%; height: 100%; display: block; opacity: 1; transition: opacity 1.2s ease;
  transition-property: opacity, --m1, --m2, --m3, --m4; transition-duration: 1.2s, .9s, .9s, .9s, .9s; }
.lit .art { transition: opacity .9s ease; }
.lit.dip .art { opacity: .35; transition-duration: .22s; }
.lit .scn { opacity: 0; transition: opacity .9s ease; }
.lit .scn.on { opacity: .6; }
.lit .glyph { font-family: 'Apple Symbols', 'Segoe UI Symbol', 'Noto Sans Symbols 2', 'DejaVu Sans', sans-serif; }
.lit .pv-glow { transform-box: fill-box; transform-origin: 50% 50%; }
.lit .pv-glow.breathe { animation: room-breathe 1.1s ease; }
.lit .lead, .lit .grain, .lit .vig { opacity: 0; transition: opacity .4s ease; }
.lit.st-stained .lead { opacity: .9; }
.lit.st-watercolor .grain, .lit.st-storybook .grain { opacity: 1; }
.lit.st-realistic .vig { opacity: 1; }
.lit .mote { animation: room-mote 2.6s ease-out forwards; opacity: 0; }
/* Choosing: the gold outline stays over the lit room. */
.has-look.is-dark .sk { opacity: .8; }
.has-look.look-real.is-dark .sk { opacity: .18; }
/* Painting: the light dims to about a tenth, so the preview flows into the painting. */
.is-drawing .lit, .is-shimmer .lit { opacity: .1; }
.is-revealing .lit { opacity: 0; transition-duration: .9s; }
.is-painted .lit { opacity: 0; }
@keyframes room-breathe { 40% { transform: scale(1.12); opacity: 1; } }
@keyframes room-mote { 0% { opacity: 0; transform: translate(0, 0); } 20% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--dx), -240px); } }
@keyframes room-draw { to { stroke-dashoffset: 0; } }
@keyframes room-shim { 50% { opacity: .45; } }
@keyframes room-eyes { from { filter: brightness(0) blur(6px); } to { filter: none; } }
@keyframes room-in { from { opacity: .4; } to { opacity: 1; } }

/* Reduced motion: crossfades only. No drawing, no breathing, no eyes-adjusting. */
.rm .sk, .rm .sk * { animation: none !important; stroke-dasharray: 1 0 !important; transition-duration: .2s; }
.rm.has-img.is-revealing .room-img { animation: room-in .4s ease both; }
.rm .lit .pv-glow, .rm .lit .mote { animation: none !important; }
.rm .lit, .rm .lit .art, .rm .lit .scn { transition-duration: .2s; }
@media (prefers-reduced-motion: reduce) {
  .sk, .sk * { animation: none !important; stroke-dasharray: 1 0 !important; }
  .lit .pv-glow, .lit .mote { animation: none !important; }
  .arrive .cv { animation: none; }
}
</style>
